import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ChevronLeft, ChevronRight, Clock, Gauge, Footprints, Activity, Timer, TrendingUp, Mountain, Zap, Waves } from "lucide-react";

// Icon + short tag per run type for quick visual scanning.
const TYPE_META = {
  "Recovery Run": { icon: Waves },
  "Base Run": { icon: Footprints },
  "Long Run": { icon: Activity },
  "Progression Run": { icon: TrendingUp },
  "Fartlek": { icon: Zap },
  "Hill Repeats": { icon: Mountain },
  "Tempo Run": { icon: Timer },
};

export default function RunWorkouts() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [filters, setFilters] = useState({ run_type: [], difficulty: [] });
  const [type, setType] = useState("All");
  const [difficulty, setDifficulty] = useState("All");

  useEffect(() => {
    api.get("/run-workouts/filters").then(({ data }) => setFilters(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setItems(null);
    const params = {};
    if (type !== "All") params.run_type = type;
    if (difficulty !== "All") params.difficulty = difficulty;
    api.get("/run-workouts", { params }).then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, [type, difficulty]);

  return (
    <AppShell
      title="Run Workouts"
      right={<Button size="sm" data-testid="back-to-run" onClick={() => navigate("/running")} className="bg-secondary border border-border text-foreground"><ChevronLeft className="h-4 w-4" /> Run</Button>}
    >
      <div data-testid="run-workouts-page" className="space-y-4">
        <p className="text-sm text-muted-foreground">Coach-built guided runs. Pick one and hit start — we’ll walk you through every interval.</p>

        <div className="grid grid-cols-2 gap-2">
          <Select value={type} onValueChange={setType}>
            <SelectTrigger data-testid="filter-run-type" className="bg-card border-border h-10"><SelectValue placeholder="Run type" /></SelectTrigger>
            <SelectContent className="bg-popover border-border text-foreground">
              <SelectItem value="All">All types</SelectItem>
              {(filters.run_type || []).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={difficulty} onValueChange={setDifficulty}>
            <SelectTrigger data-testid="filter-difficulty" className="bg-card border-border h-10"><SelectValue placeholder="Level" /></SelectTrigger>
            <SelectContent className="bg-popover border-border text-foreground">
              <SelectItem value="All">All levels</SelectItem>
              {(filters.difficulty || []).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {items === null ? (
          <div className="space-y-2">
            {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full rounded-2xl" />)}
          </div>
        ) : items.length === 0 ? (
          <Card className="rounded-2xl bg-card border-border p-8 text-center">
            <p className="text-muted-foreground text-sm">No workouts match those filters.</p>
          </Card>
        ) : (
          <div className="space-y-2.5">
            {items.map((w) => {
              const Icon = (TYPE_META[w.run_type] || {}).icon || Footprints;
              return (
                <Card
                  key={w.id}
                  data-testid={`run-workout-card-${w.id}`}
                  onClick={() => navigate(`/run-workouts/${w.id}`)}
                  className="rounded-2xl bg-card border-border p-4 cursor-pointer hover:border-[#ccff0c]/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="h-11 w-11 rounded-2xl bg-[#ccff0c]/10 border border-[#ccff0c]/20 flex items-center justify-center flex-shrink-0">
                      <Icon className="h-5 w-5 text-[#ccff0c]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-display font-bold text-base leading-tight">{w.workout_name}</p>
                        <Badge className="bg-[#ccff0c]/15 text-[#ccff0c] border border-[#ccff0c]/20 text-[10px]">{w.difficulty}</Badge>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{w.run_type}</p>
                      <p className="text-sm text-muted-foreground mt-1.5 line-clamp-2">{w.workout_card_description}</p>
                      <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                        <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {w.estimated_duration}</span>
                        <span className="flex items-center gap-1"><Gauge className="h-3.5 w-3.5" /> RPE {w.target_rpe}</span>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0 self-center" />
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppShell>
  );
}
